import { z } from 'zod';
import { SuggestionActionSchema, SuggestionKindSchema } from './suggestions';

// Activity log item captures a single apply operation summary
export const ActivityLogItemSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  suggestion_id: z.string().optional(),
  parent_id: z.string().nullable().optional(),
  kind: SuggestionKindSchema.default('category'),
  child_ids: z.array(z.string()).default([]).optional(),
  email_ids: z.array(z.string()).default([]).optional(),
  gmail_message_ids: z.array(z.string()).default([]).optional(),
  categories: z.array(z.string()).default([]).optional(),
  actions: z.array(SuggestionActionSchema).default([]),
  label: z.string().optional(),
  applied: z.boolean().default(true),
  updated_count: z.number().int().nonnegative().default(0),
  applied_at: z.string(),
  status_code: z.number().int().optional(),
  error_code: z.string().optional(),
  error_message: z.string().optional(),
  operation_id: z.string().optional(),
  /** Derived field: how this action was triggered. Added in epic #139 story #152. */
  source: z.enum(['auto_applied', 'manual']).optional(),
})

export const ActivityLogSourceSchema = z.enum(['auto_applied', 'manual', 'all'])
export type ActivityLogSource = z.infer<typeof ActivityLogSourceSchema>

export const ActivityLogActionFilterSchema = z.enum(['archive', 'trash', 'all'])
export type ActivityLogActionFilter = z.infer<typeof ActivityLogActionFilterSchema>

export const GetActivityLogsQuerySchema = z.object({
  limit: z.string().regex(/^[0-9]+$/).transform(Number).optional().default(50),
  from: z.string().optional(),
  to: z.string().optional(),
  kind: z.enum(['parent', 'category', 'custom']).optional(),
  /** Filter by how the action was triggered. Default: 'all'. Added in epic #139 story #152. */
  source: ActivityLogSourceSchema.optional().default('all'),
  action: ActivityLogActionFilterSchema.optional().default('all'),
  search: z.string().trim().min(1).max(200).optional(),
})

export const GetActivityLogsResponseSchema = z.object({
  items: z.array(ActivityLogItemSchema),
})

export const GetActivityDetailResponseSchema = ActivityLogItemSchema

export type ActivityLogItem = z.infer<typeof ActivityLogItemSchema>
export type GetActivityLogsResponse = z.infer<typeof GetActivityLogsResponseSchema>
export type GetActivityDetailResponse = z.infer<typeof GetActivityDetailResponseSchema>

// ── Auto-Applied Summary (epic #139 - dashboard card) ──────────────────────

export const AutoAppliedWindowSchema = z.enum(['7d', '30d'])
export type AutoAppliedWindow = z.infer<typeof AutoAppliedWindowSchema>

export const AutoAppliedSummaryQuerySchema = z.object({
  window: AutoAppliedWindowSchema.optional().default('7d'),
})

export const AutoAppliedSummaryItemSchema = z.object({
  id: z.string(),
  email_subject: z.string(),
  sender_name: z.string(),
  sender_email: z.string(),
  label: z.string(),
  track: z.enum(['trash', 'archive', 'attention']),
  confidence: z.number().min(0).max(1),
  applied_at: z.string(),
})
export type AutoAppliedSummaryItem = z.infer<typeof AutoAppliedSummaryItemSchema>

export const AutoAppliedSummaryResponseSchema = z.object({
  window: AutoAppliedWindowSchema,
  count: z.number().int().nonnegative(),
  /**
   * Aggregate size in bytes of all auto-applied items in the window
   * (issue #390). Best-effort sum across `emails.size_bytes` for the
   * underlying message_ids; missing emails contribute 0. Optional so
   * older clients can ignore it.
   */
  total_size_bytes: z.number().int().nonnegative().optional(),
  items: z.array(AutoAppliedSummaryItemSchema),
})
export type AutoAppliedSummaryResponse = z.infer<typeof AutoAppliedSummaryResponseSchema>

// ── Affected Emails (issue #211 - Action Insight detail) ────────────────────

export const AffectedEmailSchema = z.object({
  messageId: z.string(),
  subject: z.string().nullable(),
  fromAddress: z.string().nullable(),
  receivedAt: z.string().nullable(),
  cached: z.boolean(),
})
export type AffectedEmail = z.infer<typeof AffectedEmailSchema>

export const GetAffectedEmailsQuerySchema = z.object({
  limit: z.string().regex(/^[0-9]+$/).transform(Number).optional().default(10),
  offset: z.string().regex(/^[0-9]+$/).transform(Number).optional().default(0),
})

export const GetAffectedEmailsResponseSchema = z.object({
  emails: z.array(AffectedEmailSchema),
  total: z.number().int().nonnegative(),
})
export type GetAffectedEmailsResponse = z.infer<typeof GetAffectedEmailsResponseSchema>
